import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionButton } from 'src/routes/components';

import { useEmergencyFuse } from './hooks/useEmergencyFuse';
import type { EmergencyFuseState, FuseCardProps, SaveBarProps, TranslateFn } from './types';
import {
  buildFusePayload,
  formatUpdatedAt,
  getDefaultFuseState,
  getErrorMessage,
  isFuseStateEqual,
} from './utils';

const I18N_PREFIX = 'module.worldCup.emergencyFuse';

const tFuse = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const { state, loading, saving, refetch, updateState } = useEmergencyFuse();

  const [draft, setDraft] = useState<EmergencyFuseState>(getDefaultFuseState());
  const [originalDraft, setOriginalDraft] = useState<EmergencyFuseState>(getDefaultFuseState());

  useEffect(() => {
    setDraft(state);
    setOriginalDraft(state);
  }, [state]);

  const hasUnsavedChanges = useMemo(
    () => !isFuseStateEqual(draft, originalDraft),
    [draft, originalDraft]
  );

  const handleChange = useCallback((patch: Partial<EmergencyFuseState>) => {
    setDraft((prev) => ({
      ...prev,
      ...patch,
    }));
  }, []);

  const handleDiscard = useCallback(() => {
    if (!window.confirm(tFuse(t, 'confirm.discard'))) return;

    setDraft(originalDraft);
  }, [originalDraft, t]);

  const handleSave = useCallback(async () => {
    const reason = window.prompt(tFuse(t, 'confirm.reasonPlaceholder'));

    if (!reason?.trim()) {
      notify(tFuse(t, 'notify.reasonRequired'), { type: 'warning' });
      return;
    }

    try {
      const payload = buildFusePayload(draft, originalDraft, reason);

      await updateState(payload);
      await refetch();

      notify(tFuse(t, 'notify.saveSuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tFuse(t, 'notify.saveFailed'), { type: 'error' });
    }
  }, [draft, notify, originalDraft, refetch, t, updateState]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tFuse(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupEmergencyFuse') }]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tFuse(t, 'description')}
      </Typography>

      {loading ? (
        <CenterBox>
          <CircularProgress size={28} />
        </CenterBox>
      ) : (
        <Stack spacing={3}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              <FuseCard
                title={tFuse(t, 'pauseIssuance.title')}
                description={tFuse(t, 'pauseIssuance.desc')}
                checked={draft.pauseIssuance}
                onChange={(checked) => handleChange({ pauseIssuance: checked })}
              />

              <FuseCard
                title={tFuse(t, 'pauseOrders.title')}
                description={tFuse(t, 'pauseOrders.desc')}
                checked={draft.pauseOrders}
                onChange={(checked) => handleChange({ pauseOrders: checked })}
              />
            </Box>
          </Card>

          <MetaPanel state={originalDraft} />
        </Stack>
      )}

      {hasUnsavedChanges ? (
        <SaveBar loading={saving} onDiscard={handleDiscard} onSave={handleSave} />
      ) : null}
    </DashboardContent>
  );
};

const FuseCard = memo(({ title, description, checked, onChange }: FuseCardProps) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
    }}
  >
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    </Box>

    <Switch checked={checked} onChange={(_, value) => onChange(value)} />
  </Paper>
));

FuseCard.displayName = 'FuseCard';

const MetaPanel = memo(({ state }: { state: EmergencyFuseState }) => {
  const t = useTranslate();

  if (!state.configVersion && !state.updatedBy && !state.updatedAt && !state.reason) {
    return null;
  }

  return (
    <Card sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            {tFuse(t, 'meta.reason')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {state.reason || '--'}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            {tFuse(t, 'meta.updatedBy')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {state.updatedBy || '--'}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            {tFuse(t, 'meta.updatedAt')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatUpdatedAt(state.updatedAt)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            {tFuse(t, 'meta.configVersion')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {state.configVersion ?? '--'}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
});

MetaPanel.displayName = 'MetaPanel';

const SaveBar = memo(({ loading, onDiscard, onSave }: SaveBarProps) => {
  const t = useTranslate();

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'sticky',
        bottom: 16,
        mt: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {tFuse(t, 'saveBar.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tFuse(t, 'saveBar.desc')}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" disabled={loading} onClick={onDiscard}>
          {tFuse(t, 'actions.discard')}
        </Button>

        <PermissionButton resource="world-cup-emergency-fuse" action="write">
          <Button variant="contained" disabled={loading} onClick={onSave}>
            {loading ? t('common.saving') : tFuse(t, 'actions.save')}
          </Button>
        </PermissionButton>
      </Stack>
    </Paper>
  );
});

SaveBar.displayName = 'SaveBar';

const ErrorCard = memo(({ message }: { message: string }) => {
  const t = useTranslate();

  return (
    <Alert severity="error">
      <Typography variant="subtitle2" fontWeight={700}>
        {tFuse(t, 'error.title')}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {message}
      </Typography>
    </Alert>
  );
});

ErrorCard.displayName = 'ErrorCard';

const CenterBox = ({ children }: { children: ReactNode }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
    {children}
  </Stack>
);
